import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EnhancedSingleSelect } from "@/components/ui/enhanced-singleselect";
import { X, Plus } from "lucide-react";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react/macro";
import type { FieldConfig } from "../types";

export const LANGUAGE_LEVELS = ["mother_tongue", "good", "basic"] as const;

export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];

export interface LanguageWithLevel {
  language: string;
  level: LanguageLevel;
}

/**
 * Narrows a wire value (`models_LanguageProficiencyLevel`, a nominal string
 * enum whose members are value-identical to `LANGUAGE_LEVELS`) onto the literal
 * union this field renders. Returns `undefined` for anything else.
 */
export function toLanguageLevel(
  value: string | undefined
): LanguageLevel | undefined {
  return LANGUAGE_LEVELS.find((level) => level === value);
}

interface LanguageProficiencyFieldProps<T> {
  field: FieldConfig<T>;
  value: LanguageWithLevel[];
  onChange: (value: LanguageWithLevel[]) => void;
  disabled?: boolean;
  id: string;
}

const LEVEL_ORDER: Record<string, number> = {
  mother_tongue: 0,
  good: 1,
  basic: 2,
};

export function LanguageProficiencyField<T>({
  field,
  value,
  onChange,
  disabled,
  id,
}: LanguageProficiencyFieldProps<T>) {
  const { i18n } = useLingui();
  const [addingLanguage, setAddingLanguage] = useState(false);
  const languages = value || [];

  const options = Array.isArray(field.selectOptions)
    ? field.selectOptions
    : [];

  // Filter out already-selected languages
  const selectedCodes = new Set(languages.map((l) => l.language));
  const availableOptions = options.filter(
    (opt) => !selectedCodes.has(String(opt.value))
  );

  const levelLabels = {
    mother_tongue: i18n._(t`employees.languageLevel.motherTongue`),
    good: i18n._(t`employees.languageLevel.good`),
    basic: i18n._(t`employees.languageLevel.basic`),
  };

  const handleAddLanguage = (languageCode: string) => {
    const updated = [
      ...languages,
      { language: languageCode, level: "good" as const },
    ];
    updated.sort(
      (a, b) => (LEVEL_ORDER[a.level] ?? 2) - (LEVEL_ORDER[b.level] ?? 2)
    );
    onChange(updated);
    setAddingLanguage(false);
  };

  const handleRemoveLanguage = (languageCode: string) => {
    onChange(languages.filter((l) => l.language !== languageCode));
  };

  const handleLevelChange = (
    languageCode: string,
    newLevel: LanguageWithLevel["level"]
  ) => {
    const updated = languages.map((l) =>
      l.language === languageCode ? { ...l, level: newLevel } : l
    );
    updated.sort(
      (a, b) => (LEVEL_ORDER[a.level] ?? 2) - (LEVEL_ORDER[b.level] ?? 2)
    );
    onChange(updated);
  };

  const getLanguageLabel = (code: string) => {
    const opt = options.find((o) => String(o.value) === code);
    return opt ? `${opt.emoji ? opt.emoji + " " : ""}${opt.label}` : code;
  };

  return (
    <div id={id} className="space-y-2">
      {languages.map((lang) => (
        <div
          key={lang.language}
          className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2"
        >
          <div className="flex-1 min-w-0 space-y-1">
            <span className="block truncate text-sm">
              {getLanguageLabel(lang.language)}
            </span>
            <Select
              value={lang.level}
              onValueChange={(val) =>
                handleLevelChange(
                  lang.language,
                  val as LanguageWithLevel["level"]
                )
              }
              disabled={disabled}
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mother_tongue">
                  {levelLabels.mother_tongue}
                </SelectItem>
                <SelectItem value="good">{levelLabels.good}</SelectItem>
                <SelectItem value="basic">{levelLabels.basic}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 self-center"
            onClick={() => handleRemoveLanguage(lang.language)}
            disabled={disabled}
            aria-label={i18n._({
              id: "employees.languageLevel.removeLanguage",
              message: "Remove {language}",
              values: { language: lang.language },
            })}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ))}

      {addingLanguage ? (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <EnhancedSingleSelect
              options={availableOptions.map((opt) => ({
                value: String(opt.value),
                label: opt.label,
                emoji: opt.emoji,
                suggested: opt.suggested ?? false,
              }))}
              value=""
              onChange={(val) => {
                if (val) handleAddLanguage(val);
              }}
              placeholder={i18n._(t`employees.languageLevel.selectLanguage`)}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setAddingLanguage(false)}
            aria-label={i18n._({
              id: "employees.languageLevel.cancelAddLanguage",
              message: "Cancel adding a language",
            })}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setAddingLanguage(true)}
          disabled={disabled || availableOptions.length === 0}
        >
          <Plus className="h-4 w-4 mr-1" />
          {i18n._(t`employees.languageLevel.addLanguage`)}
        </Button>
      )}
    </div>
  );
}
